// src/pages/auth/LoginPage.tsx
import React, { useState } from "react";
import Swal from "sweetalert2";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../config/theme.config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faLock,
  faStar,
  faEnvelope,
  faTruckFast,
} from "@fortawesome/free-solid-svg-icons";
import { faGoogle } from "@fortawesome/free-brands-svg-icons";

import styles from "../../styles/auth/AuthPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const from = (location.state as any)?.from || "/customer/dashboard";

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError("Credenciales inválidas");
    }
    setLoading(false);
  };

  const handleGoogleLogin = () => {
    setLoading(true);
    setError("");

    loginWithGoogle()
      .then(({ success, error }) => {
        if (success) {
          navigate(from, { replace: true });
        } else if (error && !error.includes("cancelado")) {
          setError(error || "Error al iniciar sesión con Google");
        }
      })
      .catch((err) => {
        console.error("Error inesperado en handleGoogleLogin:", err);
        setError("Ha ocurrido un error inesperado");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className={styles.authPage}>
      <div className={`${styles.authContainer} ${styles.mediumMode}`}>
        <div className={styles.authCard}>
          <div className={styles.authHeader}>
            <h2 className={styles.authTitle}>Iniciar Sesión</h2>
            <p className={styles.authSubtitle}>Portal de acceso para clientes</p>
          </div>

          {error && <div className={`${styles.authAlert} ${styles.error}`}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label htmlFor="email" className={styles.formLabel}>
                Correo electrónico
              </label>
              <div className={styles.inputContainer}>
                <FontAwesomeIcon icon={faEnvelope} className={styles.inputIcon} />
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className={styles.authFormInput}
                  placeholder="ejemplo@correo.com"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="password" className={styles.formLabel}>
                Contraseña
              </label>
              <div className={styles.inputContainer}>
                <FontAwesomeIcon icon={faLock} className={styles.inputIcon} />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  maxLength={16}
                  className={styles.authFormInput}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={styles.inputButton}
                  title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>

            <div className={styles.forgotLinkWrapper}>
              <Link to="/forgot-password" className={styles.authLink}>
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <button type="submit" disabled={loading} className={`${btnStyles.primary} ${btnStyles.fullWidth}`} style={{ marginTop: '16px' }}>
              {loading ? (
                <div className={styles.loadingContent}>
                  <div className={styles.loadingSpinner}></div>
                  Iniciando...
                </div>
              ) : (
                <>
                  Iniciar Sesión
                  <span className={styles.buttonArrow}>→</span>
                </>
              )}
            </button>
          </form>

          <div className={styles.divider}>
            <div className={styles.dividerLine}></div>
            <div className={styles.dividerText}>o</div>
            <div className={styles.dividerLine}></div>
          </div>
          
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className={styles.googleButton}
          >
            <FontAwesomeIcon icon={faGoogle} className={styles.googleIcon} />
            Continuar con Google
          </button>

          <div className={styles.authLinks}>
            <p className={styles.authSubtitle}>
              ¿No tienes cuenta?{" "}
              <Link to="/register" className={styles.authLink} style={{ fontWeight: '600' }}>
                Regístrate aquí
              </Link>
            </p>
          </div>

          <div className={styles.authFooter}>
            <Link to="/" className={styles.backLink}>
              ← Volver al inicio
            </Link>
            <div className={styles.copyrightText}>
              © 2026 {theme.brand.name}
            </div>
          </div>
        </div>

        <div className={styles.authSideMessage}>
          <div className={styles.sideMessageContent}>
            <div className={styles.messageBadge}>
              Portal de Clientes
            </div>
            <h2>¡Bienvenido de nuevo!</h2>
            <p>
              Accede a tu cuenta para gestionar tus envíos, rastrear tus paquetes en tiempo real y disfrutar de un servicio logístico de primer nivel.
            </p>
            <div style={{ marginTop: '20px', opacity: 0.8, fontSize: '14px' }}>
              <FontAwesomeIcon icon={faTruckFast} style={{ marginRight: '10px' }} />
              Enviamos tus sueños con seguridad.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
